import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { motion } from 'motion/react';
import { press, transition } from './motion';

/**
 * Button — VERBATIM port from ~/FrontendFinal/Design/src/components/Button.tsx.
 *
 * One component, the whole matrix (5 sizes x 4 shapes x 3 states). Sizes, fills,
 * borders, and the invented hover are reverse-engineered SVG measurement — see the
 * Design repo's header for provenance; do not re-measure. The ONLY change from the
 * Design source is the import path: `../styles/motion` → `./motion` (that file was
 * ported alongside this one into the same design/ dir).
 *
 * The `border-solid` on outlined variants is KEPT VERBATIM from the Design source,
 * but it is no longer load-bearing here. Preflight is now GLOBAL (see
 * ../../../design-system.css) and its reset already sets `border-style:solid` on
 * `*,::before,::after` — verified in the built CSS — so a bare `border` would render.
 * It stays because this file is a verbatim port: the Design is the source of truth,
 * and dropping the class would make the two copies diverge for no gain.
 */

/** Shape axis — the 4 columns of the Buttons.svg grid. */
export type ButtonVariant = 'solid-square' | 'outline-square' | 'solid-pill' | 'outline-pill';

/** Size axis — the 5 row-blocks. Heights 23/32/40/47/60. */
export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

/** State axis — the 3 text-fill rows within each size block. */
export type ButtonState = 'default' | 'active' | 'disabled';

/**
 * Measured from Buttons.svg. Height, label size, and horizontal padding all scale
 * together; padding runs 12/12/16/16/20. `iconSize` is the icon-only square; `md`
 * is the one size where the square (36) differs from the button height (40).
 */
const SIZE: Record<
  ButtonSize,
  { height: string; text: string; padX: string; iconSize: string; radiusPill: string }
> = {
  xs: {
    height: 'h-[23px]',
    text: 'text-xs',
    padX: 'px-5',
    iconSize: 'w-[23px] h-[23px]',
    radiusPill: 'rounded-[12px]',
  },
  sm: {
    height: 'h-[32px]',
    text: 'text-sm',
    padX: 'px-5',
    iconSize: 'w-[32px] h-[32px]',
    radiusPill: 'rounded-lg',
  },
  md: {
    height: 'h-[40px]',
    text: 'text-md',
    padX: 'px-7',
    iconSize: 'w-[36px] h-[36px]',
    radiusPill: 'rounded-pill',
  },
  lg: {
    height: 'h-[47px]',
    text: 'text-lg',
    padX: 'px-7',
    iconSize: 'w-[47px] h-[47px]',
    radiusPill: 'rounded-pill',
  },
  xl: {
    height: 'h-[60px]',
    text: 'text-xl',
    padX: 'px-8',
    iconSize: 'w-[60px] h-[60px]',
    radiusPill: 'rounded-pill',
  },
};

/**
 * Fill and border per shape x state. `default` and `disabled` share the same rect
 * treatment and differ only in label colour; `active` changes the fill to
 * --accent-subtle.
 */
function shapeClasses(variant: ButtonVariant, state: ButtonState): string {
  const outlined = variant === 'outline-square' || variant === 'outline-pill';
  const active = state === 'active';

  if (outlined) {
    return [
      // `border-solid` is explicit and load-bearing (Preflight's border reset is
      // not in force here) — a 1px border with no style renders NOTHING.
      'border border-solid',
      'border-control',
      active ? 'bg-accent-subtle' : 'bg-transparent',
    ].join(' ');
  }
  return active ? 'bg-accent-subtle' : 'bg-surface';
}

/** Label colour is the sole carrier of the state axis in the source. */
const STATE_TEXT: Record<ButtonState, string> = {
  default: 'text-text',
  active: 'text-text-on-accent',
  disabled: 'text-text-disabled',
};

/**
 * React's DOM animation/drag handlers collide with Framer Motion's props of the
 * same names. Omitted here so the component keeps a plain-button API. `style` is
 * omitted too: motion.button's `style` is `MotionStyle` (transform keys x/y/z…),
 * not the DOM `CSSProperties`, and the two are incompatible under
 * exactOptionalPropertyTypes. The reskin never passes `style` to a Button, so we
 * drop it from the passthrough rather than reconcile the two shapes.
 */
type ConflictingHandlers =
  | 'children'
  | 'style'
  | 'onAnimationStart'
  | 'onAnimationEnd'
  | 'onAnimationIteration'
  | 'onDragStart'
  | 'onDragEnd'
  | 'onDrag';

export interface ButtonProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  ConflictingHandlers
> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  state?: ButtonState;
  /** Icon-only when `children` is omitted; leading icon when both are given. */
  icon?: ReactNode;
  children?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'solid-square',
    size = 'sm',
    state = 'default',
    icon,
    children,
    className = '',
    ...rest
  },
  ref,
) {
  const s = SIZE[size];
  const iconOnly = icon != null && children == null;
  const pill = variant === 'solid-pill' || variant === 'outline-pill';
  const disabled = state === 'disabled';

  return (
    <motion.button
      ref={ref}
      type="button"
      disabled={disabled}
      aria-disabled={disabled || undefined}
      // Press: scale only, 150ms, no translate and no shadow. Spread conditionally
      // rather than passing `undefined` — exactOptionalPropertyTypes rejects an
      // explicit undefined for the optional `whileTap`.
      {...(disabled ? {} : { whileTap: { scale: press.scale } })}
      transition={transition.press}
      className={[
        'inline-flex items-center justify-center gap-5',
        'font-light whitespace-nowrap select-none',
        // Transition colour only. No transform in CSS — Framer owns transforms.
        'transition-colors duration-micro ease-in-out',
        s.height,
        s.text,
        iconOnly ? s.iconSize : s.padX,
        pill ? s.radiusPill : 'rounded-md',
        shapeClasses(variant, state),
        STATE_TEXT[state],
        // HOVER IS INVENTED — no hover state exists in the design (audit Q7).
        disabled
          ? 'cursor-not-allowed'
          : 'hover:bg-[rgb(0_157_154_/_0.2)] hover:border-[rgb(2_43_48_/_0.5)]',
        className,
      ].join(' ')}
      {...rest}
    >
      {icon}
      {children}
    </motion.button>
  );
});
